import { InputAutocompleteAsync } from "#common/Inputs2"
import { DialogMaster } from "#core/components/dialog/Dialog"
import { useApi } from "#hooks/useApi"
import { useApiSend } from "#hooks/useApiSend"
import { jobProfileGet } from "#job-profile/services/api"
import { usePromotionsContext } from "#promotions/PromotionContext"
import { promotionIdPut } from "#promotions/services/api"
import { Box, Stack } from "@mui/material"
import Button from "@mui/material/Button"
import Checkbox from "@mui/material/Checkbox"
import FormControlLabel from "@mui/material/FormControlLabel"
import FormGroup from "@mui/material/FormGroup"
import { colaboradoresGet } from "modules/colaborador/services/api"
import { useCallback, useEffect, useMemo, useState } from "react"
import { toast } from "react-toastify"
import FormTable from "./FormTable"

/**
 * Description placeholder
 *
 * @param {Object} param0
 * @param {int} param0.colaboradorId id del colaborador que se va a solicitar
 * @param {int} param0.perfilPuestoId id del perfil de puesto que se va a solicitar
 * @param {function} param0.setOpen función para actualizar el estado de apertura del dialogo
 * @param {boolean} param0.open booleano para indicar si el dialogo se va a abrir o cerrar
 * @returns {JSX.Element} Elemento JSX que representa el componente MiEquipoPromotionDialog
 */
export default function Form({ autoFetch, value, setValue, disabled = false }) {
	const [colaboradoresAll, setColaboradoresAll] = useState(false)
	const [listMovimientos, setListMovimientos] = useState([])
	const [colaboradoresEdit, setColaboradoresEdit] = useState([])
	const [select, setSelect] = useState({ colaborador: null, perfilPuesto: null })

	const params = useMemo(() => {
		const baseParams = { fields: ["promotion_exist", "perfil_puesto"] }
		if (!colaboradoresAll) baseParams["my_collaborator"] = true
		return baseParams
	}, [colaboradoresAll])

	const { data: colaboradoresMyTeam, loading } = useApi(colaboradoresGet, {
		params,
		autoFetch,
	})
	const { data: perfilesData, loading: loadingPerfiles } = useApi(jobProfileGet, {
		autoFetch,
	})
	const perfiles = useMemo(() => perfilesData?.data || [], [perfilesData])
	const colaboradores = useMemo(() => {
		const colab = colaboradoresMyTeam?.data || []
		return colab.filter(
			(colaborador) => !value.find((mov) => mov.colaborador_id === colaborador.id),
		)
	}, [colaboradoresMyTeam?.data, value])

	useEffect(() => {
		let noSelect = colaboradores.filter((colaborador) => {
			let isNotSelect = true
			listMovimientos.forEach((element) => {
				if (element.colaborador.id === colaborador.id) isNotSelect = false
			})
			return isNotSelect
		})
		setColaboradoresEdit(noSelect)
	}, [colaboradores, listMovimientos])

	const createMovement = () => {
		if (!select.colaborador || !select.perfilPuesto) {
			toast.error("Selecciona un colaborador y un perfil")
			return
		}
		if (select.colaborador?.perfil_puesto === select.perfilPuesto?.id) {
			setSelect({ ...select, perfilPuesto: null })
			toast.error("El perfil de puesto ya lo tiene el colaborador")
		} else {
			const data = {
				colaborador: select.colaborador?.id,
				perfilPuestoNew: select.perfilPuesto?.id,
				perfilPuestoNewName: select.perfilPuesto?.nombre,
			}
			setListMovimientos([...listMovimientos, data])
			setValue([
				...value,
				{
					colaborador_id: select.colaborador.id,
					perfil_puesto_id: select.perfilPuesto.id,
				},
			])
			setSelect({ colaborador: null, perfilPuesto: null })
		}
	}

	return (
		<Box display="flex" flexDirection="column" gap={2} padding={0}>
			<Stack direction="row" spacing={2} width={"100%"} justifyContent="space-between">
				<Box width={"100%"}>
					<InputAutocompleteAsync
						label={"Colaboradores"}
						options={colaboradoresEdit}
						optionsLabel="full_name"
						loading={loading}
						value={select.colaborador}
						getOptionDisabled={(option) => option.promotion_exist}
						setValue={(e) => setSelect({ ...select, colaborador: e })}
					/>
				</Box>
				<Box width={"100%"}>
					<InputAutocompleteAsync
						label="Nuevo Perfil De Puesto"
						options={perfiles}
						optionsLabel="nombre"
						loading={loadingPerfiles}
						value={select.perfilPuesto}
						setValue={(e) => setSelect({ ...select, perfilPuesto: e })}
					/>
				</Box>
				<Button
					disabled={disabled}
					variant="contained"
					size="large"
					onClick={createMovement}
					sx={{ m: 2 }}
				>
					Agregar
				</Button>
			</Stack>
			<FormGroup>
				<FormControlLabel
					control={<Checkbox />}
					label="Todos Colaboradores"
					disabled={colaboradoresAll}
					onChange={(event) => {
						if (event.target.checked) setColaboradoresAll(true)
					}}
				/>
			</FormGroup>
			<Box>
				<FormTable
					listMovimientos={listMovimientos}
					hidden={listMovimientos.length === 0}
				/>
			</Box>
		</Box>
	)
}

export const FormDialog = ({ open, setOpen, promotion_id }) => {
	const { promotion, reload } = usePromotionsContext()
	const [value, setValue] = useState([])
	const [disabled, setDisabled] = useState(false)
	const success = () => {
		reload()
		setOpen(false)
	}
	const end = () => {
		setDisabled(false)
	}
	const { fetch } = useApiSend(promotionIdPut, { success, end })

	const onClose = useCallback(() => {
		setValue([])
	}, [])
	const handleSubmit = useCallback(() => {
		fetch(promotion_id, {
			add: true,
			details: value,
		})
	}, [fetch, promotion_id, value])

	return (
		<DialogMaster
			{...{ title: "Agregar Promoción", open, setOpen, onClose, handleSubmit, disabled }}
		>
			<Form autoFetch={open} value={value} setValue={setValue} />
		</DialogMaster>
	)
}
